import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import PageLayout from "../../components/layout/PageLayout";
import LicitacionMenu from "./LicitacionMenu";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { cn } from "../../lib/utils";
import { toast } from "sonner";

const genericQuestions = [
  {
    question:
      "¿Cuánto cobrarás por el servicio? Precio total del servicio en soles peruanos (No incluye IGV).",
    type: "Solo permite números",
  },
  {
    question: "Adjunta tu ficha RUC para validar la información de tu empresa.",
    type: "Solo archivos adjuntos",
  },
  {
    question: "¿Eres una empresa formal y puedes emitir boletas o facturas?",
    type: "Respuesta de Sí o No",
  },
  {
    question:
      "¿Qué certificaciones o capacitaciones específicas posees relacionadas con el servicio?",
    type: "Pregunta abierta",
  },
  {
    question:
      "¿Con qué seguros cuentas? ¿Seguros de responsabilidad civil, SCTR u otros?",
    type: "Pregunta abierta",
  },
];

const questionTypes = [
  "Solo permite números",
  "Pregunta abierta",
  "Respuesta de Sí o No",
  "Solo archivos adjuntos",
];

const CreateTenderStep4 = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const initialData = location.state?.formData || {};

  const [formData, setFormData] = useState({
    preguntas_licitador: initialData.preguntas_licitador || [],
    ...initialData,
  });

  const [newQuestion, setNewQuestion] = useState("");
  const [newQuestionType, setNewQuestionType] = useState("Pregunta abierta");

  const handleGenericQuestionClick = (questionObj) => {
    const isSelected = formData.preguntas_licitador.some(
      (q) => q.question === questionObj.question
    );

    const updatedQuestions = isSelected
      ? formData.preguntas_licitador.filter(
          (q) => q.question !== questionObj.question
        )
      : [...formData.preguntas_licitador, questionObj];

    if (updatedQuestions.length > 6) {
      toast.error(
        "No puedes seleccionar más de 6 preguntas. Elimina una antes de agregar otra."
      );
      return;
    }

    setFormData({
      ...formData,
      preguntas_licitador: updatedQuestions,
    });
  };

  const handleNewQuestionChange = (e) => setNewQuestion(e.target.value);
  const handleNewQuestionTypeChange = (e) => setNewQuestionType(e.target.value);

  const addNewQuestion = () => {
    if (!newQuestion.trim()) return;

    const totalQuestions = formData.preguntas_licitador.length + 1;

    if (totalQuestions > 6) {
      toast.error("No puedes añadir más de 6 preguntas.");
      return;
    }

    const newQuestionObj = { question: newQuestion, type: newQuestionType };

    const updatedQuestions = [
      ...formData.preguntas_licitador,
      newQuestionObj, // Agregar al final
    ];

    setFormData({
      ...formData,
      preguntas_licitador: updatedQuestions,
    });

    // ✅ Mostrar alerta con la pregunta agregada
    toast.success(
      `✅ Pregunta agregada correctamente\n\n❓ Pregunta: ${newQuestion}\n📌 Tipo: ${newQuestionType}`
    );

    // Limpiar los campos de entrada
    setNewQuestion("");
    setNewQuestionType("Pregunta abierta");
  };

  const handleNext = (e) => {
    e.preventDefault();

    if (formData.preguntas_licitador.length === 0) {
      toast.warning("Por favor selecciona al menos una pregunta antes de continuar.");
      return;
    }

    if (formData.isEditing) {
      // Redirige a ConfirmarEdicion con el ID de la licitación
      navigate(`/confirmar-edicion-licitacion/${formData.id_tender}`, {
        state: { formData },
      });
    } else {
      // Redirige a PreviewTender si no se está editando
      navigate("/preview-tender", { state: { formData } });
    }
  };

  const handleBack = () =>
    navigate("/create-tender-step-3", { state: { formData } });

  return (
    <PageLayout>
      <div className="mx-auto max-w-3xl px-4 py-8 sm:px-6">
        <LicitacionMenu currentStep={4} />

        <h3 className="text-xl font-semibold text-foreground">Configura las Preguntas para Postulantes</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          Elige hasta 6 preguntas para que los postulantes respondan. Escoge preguntas definidas o crea tus
          propias preguntas.
        </p>
        <form className="mt-6 flex flex-col gap-6">
          <div>
            <h4 className="text-sm font-semibold text-foreground">Añadir Pregunta Personalizada</h4>
            <div className="mt-2 flex flex-col gap-2 sm:flex-row">
              <Input
                type="text"
                placeholder="Escribe la pregunta personalizada"
                value={newQuestion}
                onChange={handleNewQuestionChange}
                className="flex-1"
              />
              <select
                value={newQuestionType}
                onChange={handleNewQuestionTypeChange}
                className="rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              >
                {questionTypes.map((type, index) => (
                  <option key={index} value={type}>
                    {type}
                  </option>
                ))}
              </select>
              <Button type="button" variant="outline" onClick={addNewQuestion}>
                Añadir Pregunta
              </Button>
            </div>

            <h4 className="mt-6 text-sm font-semibold text-foreground">Preguntas Predefinidas</h4>
            <p className="text-sm text-muted-foreground">Escoge la pregunta y el tipo de pregunta</p>

            <div className="mt-2 flex flex-col gap-2">
              {[
                // Renderizamos las preguntas genéricas primero
                ...genericQuestions,
                // Luego renderizamos las preguntas personalizadas (nuevas) al final
                ...formData.preguntas_licitador.filter(
                  (customQuestion) =>
                    !genericQuestions.some(
                      (generic) => generic.question === customQuestion.question
                    )
                ),
              ].map((q, index) => (
                <div
                  key={index}
                  onClick={() => handleGenericQuestionClick(q)}
                  className={cn(
                    "cursor-pointer rounded-md border border-border p-3 text-sm hover:bg-muted",
                    formData.preguntas_licitador.some((selected) => selected.question === q.question) &&
                      "border-primary bg-primary/5"
                  )}
                >
                  <span className="text-foreground">{q.question}</span>{" "}
                  <span className="text-muted-foreground">({q.type})</span>
                </div>
              ))}
            </div>
          </div>

          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={handleBack}>
              Retroceder
            </Button>
            <Button type="button" onClick={handleNext}>
              Siguiente
            </Button>
          </div>
        </form>
      </div>
    </PageLayout>
  );
};

export default CreateTenderStep4;
